import { useEffect } from 'react';
import { RichTextEditor as MRichTextEditor, Link } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import SubScript from '@tiptap/extension-subscript';
import { Flex } from '@mantine/core';
import Placeholder from '@tiptap/extension-placeholder';

export interface RichTextEditorProps {
  label?: string;
  required?: boolean;
  height?: number;
  value: string;
  setValue: (value: string) => void;
  error?: string;
  className?: string;
  placeholder?: string;
}

export const RichTextEditor = ({
  label,
  required = false,
  height = 206,
  value,
  setValue,
  error,
  className,
  placeholder,
  ...props
}: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      Placeholder.configure({ placeholder }),
      // @ts-ignore
      StarterKit,
      Underline,
      Link,
      Superscript,
      SubScript,
      Highlight,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setValue(html);
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value]);

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, []);

  return (
    <Flex mb={16} className={className}>
      {label && (
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
          {label}
          {required && <span style={{ color: 'red' }}> *</span>}
        </label>
      )}

      <MRichTextEditor {...props} editor={editor} style={{ height }} w='100%'>
        <MRichTextEditor.Toolbar sticky stickyOffset={60}>
          <MRichTextEditor.ControlsGroup>
            <MRichTextEditor.Bold />
            <MRichTextEditor.Italic />
            <MRichTextEditor.Underline />
            <MRichTextEditor.Strikethrough />
            <MRichTextEditor.ClearFormatting />
            <MRichTextEditor.Highlight />
            <MRichTextEditor.Code />
          </MRichTextEditor.ControlsGroup>

          <MRichTextEditor.ControlsGroup>
            <MRichTextEditor.H1 />
            <MRichTextEditor.H2 />
            <MRichTextEditor.H3 />
          </MRichTextEditor.ControlsGroup>

          <MRichTextEditor.ControlsGroup>
            <MRichTextEditor.AlignLeft />
            <MRichTextEditor.AlignCenter />
            <MRichTextEditor.AlignJustify />
            <MRichTextEditor.AlignRight />
          </MRichTextEditor.ControlsGroup>
        </MRichTextEditor.Toolbar>

        <MRichTextEditor.Content
          style={{ overflow: 'auto', maxHeight: `calc(${height}px - 3rem)` }}
        />
      </MRichTextEditor>

      {error && (
        <div style={{ color: 'red', fontSize: 14, marginTop: 4 }}>{error}</div>
      )}
    </Flex>
  );
};
